<template>
  <div class="page-admin-task-templates">
    <BaseLoadingBar is-small v-if="pending" />
    <div v-if="taskTemplates.length === 0 && !pending" class="no-templates-message-container">
      <div class="no-templates-message">
        <p class="no-template-text">{{ $t('page.admin-task-templates.no-template') }}</p>
        <p>{{ $t('page.admin-task-templates.start-by-adding') }}</p>
        <BaseButton @click.native="openAddTemplate" has-icon-left class="centered-add-button">
          <BaseIcon name="plus" />
          {{ $t('page.admin-task-templates.add-template') }}
        </BaseButton>
      </div>
    </div>
    <div v-else class="page-admin-task-templates__body">
      <!-- Phones: its main action, as the "+" of the settings header. -->
      <PageMainAction :label="$t('page.admin-task-templates.add-template')" @click="openAddTemplate" />
      <div v-if="!isPhone" class="page-admin-task-templates__table-header-actions">
        <div class="page-admin__count">
          <span class="number-monospace">{{ taskTemplates.length }}</span>
          {{ ' ' + $tc('basic-words.item', taskTemplates.length) }}
        </div>
        <div class="page-admin__actions">
          <BaseRefreshButton :size="40" :pending="pending" @refresh="onRefresh" />
          <BaseButton @click.native="openAddTemplate" has-icon-left>
            <BaseIcon name="plus" />
            {{ $t('page.admin-task-templates.add-template') }}
          </BaseButton>
        </div>
      </div>
      <div class="page-admin-task-templates__table">
        <TableNormal
          :headers="headers"
          :items="taskTemplates"
          :items-per-page="itemsToShow"
          :fixed-header="true"
          :single-select="false"
          hide-default-footer
          item-key="id"
          :mobile-breakpoint="620"
          @click:row="openEditTemplate"
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ taskTemplates.length }}</span>
            {{ ' ' + $tc('basic-words.item', taskTemplates.length) }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="onRefresh" />
          </template>
          <template v-slot:item.title="{ item }">
            <div class="page-admin-task-templates__item-title">
              {{ item.title }}
            </div>
          </template>

          <template v-slot:item.description="{ item }">
            <div class="page-admin-task-templates__item-description">
              {{ item.description }}
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <BaseMoreActions
              v-if="isPhone"
              :title="item.title"
              :actions="rowActions(item)"
              @select="onRowAction($event, item)"
            />
            <v-menu v-else>
              <template v-slot:activator="{ on, attrs }">
                <div @click.stop v-on="on" v-bind="attrs" class="menuAction">
                  <BaseIcon name="more-horizontal" class="page-admin-task-templates__actions" />
                </div>
              </template>
              <div class="page-admin-task-templates__actions-menu base-actions-menu">
                <ul>
                  <li @click="openEditTemplate(item)">
                    <BaseIcon :size="44" name="edit-2" />
                    {{ $t('actions.edit') }}
                  </li>
                  <li class="delete" @click="openDeleteTemplate(item)">
                    <BaseIcon :size="44" name="trash-2" />
                    {{ $t('actions.delete') }}
                  </li>
                </ul>
              </div>
            </v-menu>
          </template>
        </TableNormal>
      </div>
    </div>

    <TaskTemplateDialog ref="taskTemplateDialog" @action-succeeded="onRefresh" />
    <DeleteTaskTemplateDialog ref="deleteTaskTemplateDialog" @action-succeeded="onRefresh" />
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import TableNormal from '@/components/table/TableNormal.vue';
import TaskTemplateDialog from '@/components/page-admin-task-templates/TaskTemplateDialog.vue';
import DeleteTaskTemplateDialog from '@/components/page-admin-task-templates/DeleteTaskTemplateDialog.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import { mapActions, mapGetters } from 'vuex';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import { viewport } from '@/util/viewport';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';

export default {
  name: 'PageAdminTaskTemplates',
  components: {
    BaseMoreActions,
    BaseRefreshButton,
    PageMainAction,
    DeleteTaskTemplateDialog,
    TaskTemplateDialog,
    TableNormal,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
  },
  data() {
    return {
      itemsToShow: 100,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('taskTemplates', ['taskTemplates', 'pending']),
    headers() {
      return [
        { text: this.$t('page.admin-task-templates.table.title'), value: 'title' },
        { text: this.$t('page.admin-task-templates.table.description'), value: 'description', phone: 'subtitle' },
        { text: '', sortable: false, value: 'actions', align: 'end' },
      ];
    },
  },
  created() {
    this.getTaskTemplates();
  },
  methods: {
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions(item) {
      return [
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    onRowAction(key, item) {
      if (key === 'edit') this.openEditTemplate(item);
      else this.openDeleteTemplate(item);
    },
    ...mapActions('taskTemplates', ['getTaskTemplates']),
    openAddTemplate() {
      this.$refs.taskTemplateDialog.openDialog();
    },
    openEditTemplate(template) {
      if (template) {
        this.$refs.taskTemplateDialog.openDialog(template);
      }
    },
    openDeleteTemplate(template) {
      if (template) {
        this.$refs.deleteTaskTemplateDialog.openDialog(template);
      }
    },
    onRefresh() {
      this.getTaskTemplates();
    },
  },
};
</script>

<style lang="scss">
.page-admin-task-templates {
  &__body {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;
  }

  &__table-header-actions {
    padding-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__actions {
    cursor: pointer;
    margin-right: 24px;
  }

  &__actions-menu {
    cursor: pointer;
    background-color: $color-white;
  }

  // The table's type; on phones, the row's title and the line under it (BasePhoneRow).
  @include screen-from('tablet') {
    &__item-title {
      font-size: 14px;
      font-weight: 600;
      color: $color-normal;
    }

    &__item-description {
      font-size: 14px;
      color: $color-normal;
    }
  }

  .no-templates-message-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 70vh;
    width: 35%;
    margin-left: 30%;
  }

  .no-templates-message {
    text-align: center;
  }

  .no-template-text {
    font-size: 30px;
    margin-top: 30px;
    margin-bottom: 10px;
    color: $color-normal;
  }

  .centered-add-button {
    display: inline-block;
    margin-top: 8px;
  }
}

.page-admin-task-templates__table {
  td:nth-child(1) {
    width: 50%;
  }
  td:nth-child(2) {
    width: 45%;
  }
  td:nth-child(3) {
    width: 5%;
  }
}

.menuAction {
  margin-left: auto;
  width: 40px;
}
</style>
